/* eslint-disable jsx-a11y/label-has-for */
import React, { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { rem } from 'polished'
import styled from 'styled-components'
import { mediaQueryUp } from 'components/utils/breakpoint'
import type { AdultLabel } from 'lib/offer/occupancyUtils'
import { getMaxOccupancies } from 'lib/offer/occupancyUtils'
import OccupancyChildAgeSelect from './OccupancyChildAgeSelect'
import Counter from '../../../../LuxKit/Counter'
import Group from 'components/utils/Group'
import VerticalSpacer from '../Spacing/VerticalSpacer'
import InputText from 'LuxKit/Typography/InputText'
import { countWhere } from 'lib/array/arrayUtils'

const AgeContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${rem(12)} ${rem(24)};

  ${mediaQueryUp.tablet} {
    grid-template-columns: repeat(auto-fill, ${rem(124)});
    gap: ${rem(8)};
  }
`

export type OccupancyChildVariant = 'child-age' | 'infants'

interface Props extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange' | 'onReset'> {
  // occupancy objects don't have an ID so we need to provide one
  itemId: number
  occupancy: App.Occupants
  name?: string
  maxChildAge?: number
  maxInfantAge?: number
  minInfantAge?: number
  maxTeenagerAge?: number
  capacities?: Array<App.RoomCapacity>
  onChange: (itemId: number, nextOccupancy: App.Occupants, eventLabel?: string) => void
  showRemove?: boolean
  childrenAgeLabel?: string
  adultLabelType?: AdultLabel
  showChildren?: boolean
  offerType?: App.OfferType
  childVariant?: OccupancyChildVariant
  childrenLabel?: string
  showChildrenSelectLabel?: boolean
}

function OccupancyItem(props: Props) {
  const {
    maxChildAge = 17,
    minInfantAge = 0,
    maxInfantAge = -1,
    maxTeenagerAge = -1,
    itemId,
    occupancy,
    onChange,
    name,
    capacities,
    childrenAgeLabel,
    adultLabelType = 'adult',
    showChildren = true,
    offerType,
    childVariant = 'child-age',
    childrenLabel,
    showChildrenSelectLabel,
    ...rest
  } = props

  const intl = useIntl()

  const {
    adults,
    infants,
    children,
    childrenAge = [],
  } = occupancy

  const resolvedChildrenAgeLabel = childrenAgeLabel ?? intl.formatMessage({
    id: 'GBbQFyyy2x',
    defaultMessage: 'Age at time of check-in',
    description: 'Default label for children age selection in occupancy picker',
  })

  const maxCapacities = useMemo(() => {
    return getMaxOccupancies(capacities, occupancy, { maxChildAge, maxInfantAge, maxTeenagerAge })
  }, [capacities, occupancy, maxChildAge, maxInfantAge, maxTeenagerAge])

  const onAdultChange = useCallback((val: number) => {
    const newOccupancies = { ...occupancy, adults: val }
    onChange(itemId, newOccupancies, val > occupancy.adults ? 'add-adult' : undefined)
  }, [occupancy, onChange, itemId])

  const onChildrenChange = useCallback((val: number) => {
    const infantVariant = childVariant === 'infants'

    if (infantVariant) {
      const nextOccupancy = {
        ...occupancy,
        children: val,
      }

      onChange(itemId, nextOccupancy)
      return
    }

    const nextChildrenAges = [...childrenAge]
    const isIncrementing = val > nextChildrenAges.length

    if (isIncrementing) {
      nextChildrenAges.push(-1)
    } else {
      nextChildrenAges.pop()
    }

    const teenagers = countWhere(nextChildrenAges, (age) => age > maxChildAge && age <= maxTeenagerAge)
    const infants = countWhere(nextChildrenAges, (age) => age >= minInfantAge && age <= maxInfantAge)
    const children = nextChildrenAges.length - teenagers - infants

    const newOccupancies = {
      ...occupancy,
      teenagers,
      children,
      infants,
      childrenAge: nextChildrenAges,
    }

    onChange(itemId, newOccupancies, isIncrementing ? 'add-kid' : undefined)
  }, [childrenAge, maxInfantAge, maxChildAge, maxTeenagerAge, minInfantAge, childVariant, occupancy, onChange, itemId])

  const onInfantChange = useCallback((val: number) => {
    const isIncrementing = val > (infants || 0)

    const newOccupancies = {
      ...occupancy,
      infants: val,
    }

    onChange(itemId, newOccupancies, isIncrementing ? 'add-kid' : undefined)
  }, [infants, itemId, occupancy, onChange])

  const onChildrenAgeChange = useCallback((childId: number, age: number) => {
    const nextChildrenAges = [...childrenAge]
    nextChildrenAges[childId] = age

    const teenagers = countWhere(nextChildrenAges, (age) => age > maxChildAge && age <= maxTeenagerAge)
    const infants = countWhere(nextChildrenAges, (age) => age >= minInfantAge && age <= maxInfantAge)
    const children = nextChildrenAges.length - teenagers - infants

    const newOccupancy = {
      ...occupancy,
      children,
      teenagers,
      infants,
      childrenAge: nextChildrenAges,
    }
    onChange(itemId, newOccupancy)
  }, [childrenAge, occupancy, onChange, maxInfantAge, maxChildAge, maxTeenagerAge, minInfantAge, itemId])

  const onlyInfantCanBeAdded = maxCapacities.children === 0 && (maxCapacities.infants ?? 0) > 0
  const minAdultAge = onlyInfantCanBeAdded ? maxInfantAge : Math.max(maxTeenagerAge, maxChildAge) + 1
  const adultsLabel = intl.formatMessage({
    id: 'di0q/1FXrL',
    defaultMessage: 'Adults',
    description: 'Label for adult traveller count in occupancy picker',
  })
  const travellersLabel = intl.formatMessage({
    id: '3rmZR0JLmc',
    defaultMessage: 'Travellers',
    description: 'Form field label for traveller count in the occupancy picker',
  })
  const adultLabel = adultLabelType === 'adult' ? adultsLabel : travellersLabel
  const showChildrenAge = childVariant === 'child-age' ? childrenAge.length > 0 : false
  const infantVariant = childVariant === 'infants'

  const childrenAgeSubLabel = intl.formatMessage({
    id: '4M+q8kQJC4',
    defaultMessage: '({label})',
    description: 'Parenthetical sub-label showing children age requirement in occupancy picker',
  }, { label: resolvedChildrenAgeLabel })

  return (
    <VerticalSpacer {...rest} gap={24} data-testid={`occupancy-item-${itemId}`}>
      <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center">
        <label>
          <InputText variant="label">{adultLabel}</InputText>
          <InputText variant="sub-label" colour="neutral-three">{intl.formatMessage({
            id: 'tmsFo0DiiP',
            defaultMessage: 'Ages {minAdultAge} and above',
            description: 'Minimum age requirement label in the occupancy picker',
          }, { minAdultAge })}
          </InputText>
        </label>

        <Counter
          data-testid={`occupancy-item-${itemId}-adults`}
          value={adults}
          onChange={onAdultChange}
          max={maxCapacities.adults}
          min={1}
          name={`${name}.adults`}
        />
      </Group>

      {showChildren && <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center">
        <label>
          <InputText variant="label">{intl.formatMessage({
            id: '2XvNpEz1Cw',
            defaultMessage: 'Children',
            description: 'Label for children count in occupancy picker in the occupancy picker',
          })}
          </InputText>
          <InputText variant="sub-label" colour="neutral-three">{childrenLabel || intl.formatMessage({
            id: '+3NiyI7Ej0',
            defaultMessage: 'Up to {minAdultAge} years old',
            description: 'Maximum age label for children in occupancy picker',
          }, { minAdultAge })}
          </InputText>
        </label>

        <Counter
          data-testid={`occupancy-item-${itemId}-children`}
          value={infantVariant ? children : childrenAge.length}
          onChange={onChildrenChange}
          max={childVariant === 'infants' ? maxCapacities.children : (maxCapacities.teenagers ?? 0) + (maxCapacities.children ?? 0) + (maxCapacities.infants ?? 0)}
          min={0}
          name={`${name}.children`}
        />
      </Group>}

      {infantVariant && <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center">
        <label>
          <InputText variant="label">{intl.formatMessage({
            id: '6w7w/nUSox',
            defaultMessage: 'Infants',
            description: 'Label for infant count in occupancy picker in the occupancy picker',
          })}
          </InputText>
          <InputText variant="sub-label" colour="neutral-three">{intl.formatMessage({
            id: 'BEAiZpNUfs',
            defaultMessage: 'Aged under {maxAge} \u00B7 On lap',
            description: 'Age limit and seating info for infants in occupancy picker',
          }, { maxAge: maxInfantAge + 1 })}
          </InputText>
        </label>

        <Counter
          data-testid={`occupancy-item-${itemId}-infant`}
          value={infants}
          onChange={onInfantChange}
          max={maxCapacities.infants ?? 0}
          min={0}
          name={`${name}.infants`}
        />
      </Group>}

      {showChildrenAge &&
        <VerticalSpacer gap={8}>
          <label>
            <InputText variant="label">{intl.formatMessage({
              id: 'I1FE3QNxvC',
              defaultMessage: 'Children’s ages',
              description: 'Heading for children age selection section in occupancy picker',
            })}
            </InputText>
            <InputText variant="sub-label" colour="neutral-three">{childrenAgeSubLabel}</InputText>
          </label>

          <AgeContainer>
            {childrenAge.map((child, index) => <OccupancyChildAgeSelect
              key={index}
              capacities={capacities}
              occupancy={occupancy}
              maxChildAge={maxChildAge}
              maxInfantAge={maxInfantAge}
              maxTeenagerAge={maxTeenagerAge}
              childId={index}
              defaultValue={child === -1 ? undefined : child}
              onChange={onChildrenAgeChange}
              data-testid={`occupancy-item-${itemId}-childrenAge-${index}`}
              name={`${name}.childrenAge[${index}]::number`}
              childrenAgeLabel={resolvedChildrenAgeLabel}
              showChildrenSelectLabel={showChildrenSelectLabel}
              required
            />)}
          </AgeContainer>
        </VerticalSpacer>}
    </VerticalSpacer>
  )
}

export default React.memo(OccupancyItem)
